import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot, User, Mail, Phone, ChevronRight, ArrowLeft } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { createLead, sendChatMessage, aiChat, logIntent } from "@/lib/api";
import { openExternal } from "@/lib/openExternal";
import { useSettings } from "@/context/SettingsContext";

/**
 * Compact, round floating chat icon. Magnetises to left/right edge on drag.
 * - Online: one-shot "leave a message" form.
 * - Offline: visitor can either leave a message OR run a guided AI finance helper
 *   which asks 5–6 scripted questions and submits a lead tagged source='ai_assistant'.
 */
export default function ChatWidget() {
    const { pathname } = useLocation();
    const { settings } = useSettings();

    const [open, setOpen] = useState(false);
    const BUBBLE_W = 180;
    const BUBBLE_H = 52;
    const EDGE = 18;

    const getInitialX = () => {
        const saved = localStorage.getItem("acumen_chat_side");
        if (saved === "left") return EDGE;
        return Math.max(EDGE, window.innerWidth - BUBBLE_W - EDGE);
    };
    const [x, setX] = useState(getInitialX);
    const [top, setTop] = useState(() => {
        const saved = Number(localStorage.getItem("acumen_chat_top"));
        return Number.isFinite(saved) && saved > 60
            ? saved
            : Math.max(window.innerHeight - 110, 200);
    });
    const [dragging, setDragging] = useState(false);
    const startRef = useRef({ px: 0, py: 0, x: 0, top: 0, moved: false });

    const [mode, setMode] = useState("hub"); // hub | message | ai
    const online = !!settings?.chat_online;
    const adviser = settings?.adviser_name || "Acumen Adviser";
    const phone = settings?.phone || "07949 541996";
    const welcome =
        settings?.chat_welcome ||
        (online
            ? "An adviser is online — leave a message and we'll reply."
            : "We're currently offline. Leave a message for an adviser, or chat with our AI finance helper for instant guidance.");

    // Keep bar within viewport on resize
    useEffect(() => {
        const onResize = () => {
            setX((curX) => Math.min(Math.max(EDGE, curX), window.innerWidth - BUBBLE_W - EDGE));
            setTop((curT) => Math.min(Math.max(80, curT), window.innerHeight - BUBBLE_H - 20));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const onPointerDown = (e) => {
        if (open) return;
        const pt = e.touches ? e.touches[0] : e;
        startRef.current = { px: pt.clientX, py: pt.clientY, x, top, moved: false };
        setDragging(true);
    };

    useEffect(() => {
        if (!dragging) return;
        const move = (e) => {
            const pt = e.touches ? e.touches[0] : e;
            const dx = pt.clientX - startRef.current.px;
            const dy = pt.clientY - startRef.current.py;
            if (Math.abs(dy) + Math.abs(dx) > 4) startRef.current.moved = true;
            const nextX = Math.max(
                EDGE,
                Math.min(window.innerWidth - BUBBLE_W - EDGE, startRef.current.x + dx)
            );
            const nextTop = Math.max(
                80,
                Math.min(window.innerHeight - BUBBLE_H - 20, startRef.current.top + dy)
            );
            setX(nextX);
            setTop(nextTop);
        };
        const up = () => {
            setDragging(false);
            if (!startRef.current.moved) {
                setOpen(true);
                setMode("hub");
                return;
            }
            // Magnetise: snap to nearest horizontal edge with a smooth transition
            const center = x + BUBBLE_W / 2;
            const isLeft = center < window.innerWidth / 2;
            const snappedX = isLeft ? EDGE : window.innerWidth - BUBBLE_W - EDGE;
            setX(snappedX);
            localStorage.setItem("acumen_chat_side", isLeft ? "left" : "right");
            localStorage.setItem("acumen_chat_top", String(top));
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        window.addEventListener("touchmove", move, { passive: true });
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", up);
        };
    }, [dragging, x, top]);

    if (pathname.startsWith("/admin")) return null;

    const side = x < window.innerWidth / 2 ? "left" : "right";

    return (
        <>
            {/* Floating pill-bar: "Get in touch" */}
            <button
                data-testid="chat-bubble"
                aria-label="Get in touch"
                onMouseDown={onPointerDown}
                onTouchStart={onPointerDown}
                onClick={(e) => {
                    if (startRef.current.moved) {
                        startRef.current.moved = false;
                        e.preventDefault();
                        return;
                    }
                    setOpen(true);
                    setMode("hub");
                }}
                className="fixed z-[60] flex select-none items-center gap-3 bg-white text-brand-primary pl-3 pr-5 rounded-full shadow-[0_18px_40px_-10px_rgba(12,26,46,0.35)] border border-brand-border hover:bg-brand-bg will-change-transform"
                style={{
                    top,
                    left: x,
                    height: BUBBLE_H,
                    cursor: dragging ? "grabbing" : "grab",
                    transition: dragging
                        ? "none"
                        : "left 0.5s cubic-bezier(0.22, 1, 0.36, 1), top 0.5s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s",
                    touchAction: "none",
                }}
            >
                <span className="relative h-9 w-9 rounded-full bg-brand-primary flex items-center justify-center text-white">
                    <MessageCircle size={17} />
                    <span
                        data-testid="chat-status-dot"
                        className={`absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                            online ? "bg-[#5BD27A] animate-pulse" : "bg-[#D95555]"
                        }`}
                    />
                </span>
                <span className="font-bold text-sm tracking-wide whitespace-nowrap">
                    Get in touch
                </span>
            </button>

            {open && (
                <ChatPanel
                    side={side}
                    top={top}
                    adviser={adviser}
                    online={online}
                    welcome={welcome}
                    phone={phone}
                    mode={mode}
                    setMode={setMode}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
}

function ChatPanel({ side, top, adviser, online, welcome, phone, mode, setMode, onClose }) {
    const PANEL_W = 400;
    const PANEL_H_MAX = 640;

    // Initial position based on the bubble side
    const initialPos = () => {
        if (typeof window === "undefined") return { left: 18, top: 100 };
        const isMobile = window.innerWidth < 640;
        if (isMobile) {
            return {
                left: 8,
                top: Math.max(70, Math.min(top, window.innerHeight - 0.85 * window.innerHeight)),
            };
        }
        const left =
            side === "left"
                ? 18
                : Math.max(18, window.innerWidth - PANEL_W - 18);
        return {
            left,
            top: Math.max(70, Math.min(top, window.innerHeight - PANEL_H_MAX - 20)),
        };
    };

    const [pos, setPos] = useState(initialPos);
    const [dragging, setDragging] = useState(false);
    const dragStartRef = useRef({ px: 0, py: 0, left: 0, top: 0 });

    const onHeaderPointerDown = (e) => {
        // Don't start drag when interacting with header buttons
        if (e.target.closest("button")) return;
        const pt = e.touches ? e.touches[0] : e;
        dragStartRef.current = {
            px: pt.clientX,
            py: pt.clientY,
            left: pos.left,
            top: pos.top,
        };
        setDragging(true);
    };

    useEffect(() => {
        if (!dragging) return;
        const move = (e) => {
            const pt = e.touches ? e.touches[0] : e;
            const dx = pt.clientX - dragStartRef.current.px;
            const dy = pt.clientY - dragStartRef.current.py;
            const isMobile = window.innerWidth < 640;
            const w = isMobile ? window.innerWidth - 16 : PANEL_W;
            const h = Math.min(
                isMobile ? window.innerHeight * 0.85 : PANEL_H_MAX,
                window.innerHeight - 80
            );
            const nextLeft = Math.max(
                4,
                Math.min(window.innerWidth - w - 4, dragStartRef.current.left + dx)
            );
            const nextTop = Math.max(
                60,
                Math.min(window.innerHeight - h - 20, dragStartRef.current.top + dy)
            );
            setPos({ left: nextLeft, top: nextTop });
            if (e.cancelable) e.preventDefault();
        };
        const up = () => setDragging(false);
        window.addEventListener("mousemove", move);
        window.addEventListener("touchmove", move, { passive: false });
        window.addEventListener("mouseup", up);
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchend", up);
        };
    }, [dragging]);

    return (
        <div
            data-testid="chat-panel"
            className="fixed z-[70] bg-brand-surface border border-brand-border shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] flex flex-col overflow-hidden rounded-[12px]"
            style={{
                left: pos.left,
                top: pos.top,
                width: "calc(100vw - 16px)",
                maxWidth: `${PANEL_W}px`,
                height: "85vh",
                maxHeight: `${PANEL_H_MAX}px`,
                cursor: dragging ? "grabbing" : "default",
                userSelect: dragging ? "none" : "auto",
            }}
        >
            <div
                onMouseDown={onHeaderPointerDown}
                onTouchStart={onHeaderPointerDown}
                data-testid="chat-panel-header"
                className="bg-brand-primary text-white px-5 py-4 flex items-center justify-between shrink-0 select-none"
                style={{ cursor: "grab", touchAction: "none" }}
            >
                <div className="flex items-center gap-3">
                    {mode !== "hub" && (
                        <button
                            onClick={() => setMode("hub")}
                            data-testid="chat-back"
                            className="text-white/85 hover:text-white"
                            aria-label="Back"
                        >
                            <ArrowLeft size={16} />
                        </button>
                    )}
                    <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0">
                        {mode === "ai" ? (
                            <Bot size={18} className="text-brand-primary" />
                        ) : (
                            <MessageCircle size={18} className="text-brand-primary" />
                        )}
                    </div>
                    <div>
                        <div className="font-bold text-sm">
                            {mode === "ai" ? "AI Finance Helper" : adviser}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-white/75">
                            <span
                                className={`h-2 w-2 rounded-full ${online ? "bg-[#5BD27A]" : "bg-[#D95555]"}`}
                            />
                            {mode === "ai"
                                ? "Instant answers · powered by AI"
                                : online
                                    ? "Online now"
                                    : "Offline — reply within working hours"}
                        </div>
                    </div>
                </div>
                <button
                    data-testid="chat-close"
                    onClick={onClose}
                    className="text-white/80 hover:text-white"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
                {mode === "hub" && (
                    <div className="flex-1 overflow-y-auto">
                        <HubView online={online} welcome={welcome} phone={phone} setMode={setMode} />
                    </div>
                )}
                {mode === "message" && <MessageView onDone={onClose} online={online} />}
                {mode === "ai" && <AIFlowView onDone={onClose} />}
            </div>
        </div>
    );
}

function HubView({ online, welcome, phone, setMode }) {
    const waLink = "https://wa.me/447949541996?text=Hi%20Acumen%20%E2%80%94%20I%27d%20like%20mortgage%20advice.";
    const telHref = `tel:${(phone || "").replace(/\s+/g, "")}`;
    return (
        <div className="p-5 space-y-4">
            <p className="text-sm text-brand-muted leading-relaxed">{welcome}</p>

            <div className="space-y-3">
                {!online && (
                    <button
                        data-testid="chat-start-ai"
                        onClick={() => {
                            logIntent("ai_helper");
                            setMode("ai");
                        }}
                        className="w-full group flex items-center justify-between bg-brand-primary text-brand-bg px-5 py-4 hover:bg-brand-primaryHover transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center">
                                <Bot size={16} className="text-brand-primary" />
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-sm">AI Finance Helper</div>
                                <div className="text-[11px] text-white/75">
                                    Answer 6 quick questions, get guidance now
                                </div>
                            </div>
                        </div>
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                )}

                <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.preventDefault();
                        logIntent("whatsapp");
                        openExternal(waLink);
                    }}
                    data-testid="chat-start-whatsapp"
                    className="w-full group flex items-center justify-between px-5 py-4 transition-colors bg-[#25D366] text-white hover:bg-[#1FB856] cursor-pointer"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center">
                            <WhatsAppGlyph size={18} />
                        </div>
                        <div className="text-left">
                            <div className="font-bold text-sm">Chat on WhatsApp</div>
                            <div className="text-[11px] text-white/85">
                                Instant reply · +44 7949 541996
                            </div>
                        </div>
                    </div>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>

                <a
                    href={telHref}
                    onClick={() => logIntent("call")}
                    data-testid="chat-start-call"
                    className="w-full group flex items-center justify-between px-5 py-4 transition-colors bg-brand-primary text-white hover:bg-brand-primaryHover cursor-pointer"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center">
                            <Phone size={16} className="text-brand-primary" />
                        </div>
                        <div className="text-left">
                            <div className="font-bold text-sm">Call us</div>
                            <div className="text-[11px] text-white/85">
                                Tap to dial · {phone}
                            </div>
                        </div>
                    </div>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>

                <button
                    data-testid="chat-start-message"
                    onClick={() => {
                        logIntent("message");
                        setMode("message");
                    }}
                    className={`w-full group flex items-center justify-between px-5 py-4 transition-colors ${
                        online
                            ? "bg-brand-primary text-brand-bg hover:bg-brand-primaryHover"
                            : "bg-brand-surface text-brand-primary border border-brand-border hover:border-brand-primary"
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-full ${online ? "bg-white" : "bg-brand-primary/10"} flex items-center justify-center`}>
                            <Mail size={16} className="text-brand-primary" />
                        </div>
                        <div className="text-left">
                            <div className="font-bold text-sm">
                                {online ? "Chat with adviser" : "Leave a message"}
                            </div>
                            <div className={`text-[11px] ${online ? "text-white/75" : "text-brand-muted"}`}>
                                {online
                                    ? "An adviser is ready to reply"
                                    : "We'll reply in working hours"}
                            </div>
                        </div>
                    </div>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
    );
}

function WhatsAppGlyph({ size = 18 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="#25D366"
            aria-hidden="true"
        >
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.821 11.821 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.554-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.372-.025-.521-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.711.306 1.265.489 1.697.626.713.226 1.362.194 1.875.118.572-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
        </svg>
    );
}

function MessageView({ onDone, online }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please complete all fields.");
            return;
        }
        setSending(true);
        try {
            await sendChatMessage(form);
            toast.success(
                online
                    ? "Sent — adviser will reply shortly."
                    : "Message received — we'll be in touch."
            );
            onDone();
        } catch {
            toast.error("Could not send. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="chat-form" className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="chat-name"
                    className="w-full bg-transparent border-b border-brand-border py-2 text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none"
                />
                <input
                    type="email"
                    required
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="chat-email"
                    className="w-full bg-transparent border-b border-brand-border py-2 text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none"
                />
                <input
                    type="tel"
                    required
                    placeholder="Phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="chat-phone"
                    className="w-full bg-transparent border-b border-brand-border py-2 text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none"
                />
                <textarea
                    rows={3}
                    required
                    placeholder="How can we help?"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="chat-message"
                    className="w-full bg-transparent border-b border-brand-border py-2 text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none resize-none"
                />
            </div>
            <div className="p-3 border-t border-brand-border bg-brand-surface shrink-0 space-y-2">
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="chat-submit"
                    className="w-full bg-brand-primary text-white px-5 py-3 rounded-md flex items-center justify-center gap-2 text-sm font-bold hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                >
                    {sending ? "Sending…" : (<><Send size={14} /> Send</>)}
                </button>
                <p className="text-[10px] text-brand-muted leading-snug">
                    By submitting your details, you confirm you have read and
                    understood our{" "}
                    <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-brand-primary hover:text-brand-accent"
                    >
                        Privacy Policy
                    </a>
                    .
                </p>
            </div>
        </form>
    );
}

/** Free-form AI finance helper — calls /api/chat/ai (LLM via Emergent key). */
function AIFlowView({ onDone }) {
    const [sessionId] = useState(
        () => `ai-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    );
    const [thread, setThread] = useState([
        {
            role: "bot",
            text:
                "Hi there — I'm Acumen Adviser, your AI finance helper. A regulated adviser will be in touch shortly. To help them prepare, could you share a few details:\n\n" +
                "• What are you hoping to do? (first-time buy, moving home, remortgage, buy-to-let, bridging, protection)\n" +
                "• Your name and rough property value / amount you'd like to borrow\n" +
                "• Your city or postcode\n\n" +
                "Or just ask me anything about UK mortgages — I'll point you in the right direction.",
        },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const scrollerRef = useRef(null);
    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [thread, sending]);

    const send = async (e) => {
        e?.preventDefault?.();
        const text = input.trim();
        if (!text || sending) return;
        setInput("");
        setThread((t) => [...t, { role: "user", text }]);
        setSending(true);
        try {
            const r = await aiChat(sessionId, text);
            setThread((t) => [...t, { role: "bot", text: r.reply }]);
        } catch {
            setThread((t) => [
                ...t,
                {
                    role: "bot",
                    text:
                        "Sorry — I couldn't reach the AI just now. Please try " +
                        "again, or use the Contact form.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const QUICK_PROMPTS = [
        "First-time buyer",
        "Moving home",
        "Remortgage",
        "Buy-to-let",
        "How much can I borrow?",
    ];

    const wa = "https://wa.me/447949541996?text=Hi%20Acumen%20%E2%80%94%20I%27d%20like%20mortgage%20advice.";

    return (
        <div className="flex flex-col flex-1 min-h-0" data-testid="chat-ai-flow">
            <div
                ref={scrollerRef}
                className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-brand-bg/40"
            >
                {thread.map((m, i) => (
                    <div
                        key={i}
                        className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                        {m.role === "bot" && (
                            <div className="h-7 w-7 rounded-full bg-brand-primary flex items-center justify-center shrink-0">
                                <Bot size={13} className="text-white" />
                            </div>
                        )}
                        <div
                            className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed rounded-lg whitespace-pre-wrap ${
                                m.role === "user"
                                    ? "bg-brand-primary text-white font-semibold"
                                    : "bg-brand-surface border border-brand-border text-brand-primary"
                            }`}
                        >
                            {m.text}
                        </div>
                        {m.role === "user" && (
                            <div className="h-7 w-7 rounded-full bg-brand-primary flex items-center justify-center shrink-0">
                                <User size={13} className="text-white" />
                            </div>
                        )}
                    </div>
                ))}
                {sending && (
                    <div className="flex gap-2 justify-start">
                        <div className="h-7 w-7 rounded-full bg-brand-primary flex items-center justify-center shrink-0">
                            <Bot size={13} className="text-white" />
                        </div>
                        <div className="bg-brand-surface border border-brand-border text-brand-primary px-3.5 py-2.5 rounded-lg text-sm">
                            <span className="inline-flex gap-1">
                                <span className="h-2 w-2 rounded-full bg-brand-primary animate-pulse" />
                                <span
                                    className="h-2 w-2 rounded-full bg-brand-primary animate-pulse"
                                    style={{ animationDelay: "150ms" }}
                                />
                                <span
                                    className="h-2 w-2 rounded-full bg-brand-primary animate-pulse"
                                    style={{ animationDelay: "300ms" }}
                                />
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick suggestion chips on first turn only */}
            {thread.length === 1 && !sending && (
                <div className="px-3 pt-2 flex flex-wrap gap-2 shrink-0">
                    {QUICK_PROMPTS.map((p) => (
                        <button
                            key={p}
                            onClick={() => setInput(p)}
                            data-testid={`ai-quickprompt-${p.slice(0, 12).replace(/\s/g, "-")}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-brand-border text-brand-primary hover:bg-brand-primary hover:text-white transition-colors"
                        >
                            {p}
                        </button>
                    ))}
                </div>
            )}

            <form
                onSubmit={send}
                data-testid="ai-chat-form"
                className="p-3 border-t border-brand-border bg-brand-surface shrink-0 space-y-2"
            >
                <div className="flex items-center gap-2 border border-brand-border rounded-lg px-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask anything about UK mortgages…"
                        data-testid="ai-chat-input"
                        className="flex-1 bg-transparent py-2 text-sm text-brand-primary placeholder-brand-muted outline-none"
                    />
                    <button
                        type="submit"
                        disabled={!input.trim() || sending}
                        data-testid="ai-chat-send"
                        className="h-8 w-8 flex items-center justify-center rounded-md bg-brand-primary text-white disabled:opacity-50 hover:bg-brand-primaryHover transition-colors"
                        aria-label="Send"
                    >
                        <Send size={14} />
                    </button>
                </div>
                <div className="flex items-center justify-between text-[11px] text-brand-muted">
                    <span>Replies are AI-assisted. Final advice from a regulated adviser.</span>
                    <a
                        href={wa}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            e.preventDefault();
                            logIntent("whatsapp");
                            openExternal(wa);
                        }}
                        data-testid="ai-chat-whatsapp"
                        className="font-bold text-brand-primary hover:underline cursor-pointer"
                    >
                        WhatsApp
                    </a>
                </div>
            </form>
        </div>
    );
}
